<template>
  <f7-block-title>{{ $t('campaign.auctionInProgress') }}</f7-block-title>
  <f7-block class="bid-summary">
    <div class="bid-current">{{ fmt(highest?.amount ?? reservePrice) }}</div>
    <div class="bid-caption">
      {{ highest ? $t('campaign.highestBid') : $t('campaign.reservePrice') }}
      <span v-if="highest"> — {{ maskUser(highest.userId) }}</span>
    </div>
  </f7-block>

  <f7-list inset strong outline >
    <f7-list-input
      :label="$t('campaign.yourBid')"
      type="number"
      :placeholder="String(minimumRequired)"
      :value="bidAmount"
      @input="bidAmount = $event.target.value"
    ><template #media>{{ currentCampaign?.currency }}</template></f7-list-input>
  </f7-list>
  <f7-block>
    <f7-button large round raised fill :disabled="!canBid" @click="placeBid">
      {{ $t('campaign.bidMinimum', { amount: fmtCfa(minimumRequired) }) }}
    </f7-button>
  </f7-block>
</template>

<script setup>
import { ref, computed, onMounted } from 'vue'
import { f7 } from 'framework7-vue'
import client from '../../js/api/client.js'
import { useKeycloakStore } from '../../js/pinia-stores/keycloak.js'
import { useCampaignsStore } from '../../js/pinia-stores/campaign.js' 
import i18n from '../../js/i18n.js'

const { t } = i18n.global

const props = defineProps({
  campaignId: { type: String, required: true },
  reservePrice: { type: Number, default: 0 },
  bidIncrement: { type: Number, default: 1000 },
})
const emit = defineEmits(['bid-placed'])

const keycloak = useKeycloakStore()
const campaignsStore = useCampaignsStore()
const bids = ref([])
const bidAmount = ref('')
const currentCampaign= computed(() => campaignsStore.activeCampaign);
const highest = computed(() => bids.value[0] ?? null)
const minimumRequired = computed(() =>
  highest.value ? highest.value.amount + props.bidIncrement : props.reservePrice)
const canBid = computed(() =>
  Number(bidAmount.value) >= minimumRequired.value)

function fmtCfa(n) { return `${Math.round(n).toLocaleString('fr-FR')} ${currentCampaign.value?.currency}` }
function maskUser(id) { return `${t('campaign.member')} ${String(id).slice(0, 4)}…` }

onMounted(fetchBids)

async function fetchBids() {
  const { data } = await client.get(`/campaigns/${props.campaignId}/bids`)
  bids.value = data
}

async function placeBid() {
  f7.preloader.show()
  try {
    const { data } = await client.post(
      `/campaigns/${props.campaignId}/bids`, {
        userId: keycloak.subject,
        amount: Number(bidAmount.value),
      })
    f7.preloader.hide()
    bidAmount.value = ''
    await fetchBids()
    emit('bid-placed', data)
    f7.toast.show({ text: t('campaign.bidPlaced'), closeTimeout: 2000 })
  } catch (e) {
    f7.preloader.hide()
    f7.dialog.alert(e?.response?.data?.message || t('campaign.bidTooLow'))
  }
}
</script>

<style scoped>
.bid-summary { text-align: center; }
.bid-current { font-size: 26px; font-weight: 500; color: var(--f7-theme-color); }
.bid-caption { font-size: 12px; color: var(--f7-on-surface-variant, #8e8e93); margin-top: 2px; }
</style>
